import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OperonLogo } from "@/components/OperonLogo";
import { useTheme } from "@/lib/theme";
import { api, extractErr } from "@/lib/api";
import { Sun, Moon, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const { theme, toggleTheme } = useTheme();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords don’t match"); return; }
    if (pw.length < 6) { toast.error("Password too short"); return; }
    if (!token) { toast.error("Invalid reset link"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (e) {
      toast.error(extractErr(e));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <Link to="/" className="flex items-center"><OperonLogo theme={theme} size={32} /></Link>
        <Button variant="ghost" size="icon" onClick={toggleTheme}>
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>
      </div>
      <div className="flex-1 flex items-center justify-center px-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="reset-password-form">
          {done ? (
            <div className="rounded-xl border border-border bg-card p-5 text-center space-y-3">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
              <h1 className="font-heading text-xl font-semibold">Password updated</h1>
              <p className="text-sm text-muted-foreground">Redirecting to login…</p>
            </div>
          ) : (
            <>
              <div>
                <h1 className="font-heading text-2xl font-semibold tracking-tight">Set a new password</h1>
                <p className="text-sm text-muted-foreground mt-1">Choose a strong password of 6+ characters.</p>
              </div>
              <div className="space-y-1.5"><Label>New password</Label><Input type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" /></div>
              <div className="space-y-1.5"><Label>Confirm password</Label><Input type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm-input" /></div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-password-submit">{loading ? "Saving…" : "Update password"}</Button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
